<template>
    <section class="articles-list">
        <div class="slice-content">
            <div class="content">
                <PrismicRichText :field="slice.primary.title" class="title" />
                <PrismicRichText :field="slice.primary.headline" class="headline" />
            </div>
            <div class="articles">
                <div class="content">
                    <div class="articles-grid">
                        <ArticleCard v-for="article in articles" :key="article.uid" :article="article" />
                    </div>
                    <div class="button-row">
                        <NuxtLink v-slot="{href, navigate}" :to="$withLocalePrefix('/resources/articles')" custom>
                            <VJoyButton variant="main" :href="href" @click="navigate">{{ t('resources.articles.seeAll') }}</VJoyButton>
                        </NuxtLink>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {isFilled} from '@prismicio/helpers';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import {PrismicRichText} from '@prismicio/vue';
    import type {ArticleDocument, ArticleDocumentData, ArticlesListSliceDefault} from '@prismicio-types';
    import {useAsyncData, useTranslation} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import ArticleCard from '~/components/ArticleCard.vue';
    import usePrismicSearch from '~/composables/content/usePrismicSearch';

    const props = defineProps<{slice: ArticlesListSliceDefault}>();

    const articles = [props.slice.primary.article1, props.slice.primary.article2, props.slice.primary.article3].filter((article) =>
        isFilled.contentRelationship<'article', string, ArticleDocumentData>(article),
    ) as (FilledContentRelationshipField<'article', string, ArticleDocumentData> | ArticleDocument)[];

    const {t} = useTranslation();

    function fetchArticles() {
        if (articles.length < 3) {
            // prismic does not allow 'or' queries, so we have to do multiple queries
            const searchQueries = props.slice.items
                .filter((searchFilter) => searchFilter.categoryFilter)
                .map((item) =>
                    usePrismicSearch({
                        type: 'article',
                        pageSize: 3 - articles.length,
                        category: item.categoryFilter ?? undefined,
                        excludes: articles.map((article) => article.id),
                    }),
                );
            if (searchQueries.length === 0) {
                searchQueries.push(
                    usePrismicSearch({
                        type: 'article',
                        pageSize: 3 - articles.length,
                        excludes: articles.map((article) => article.id),
                    }),
                );
            }

            return Promise.all(searchQueries);
        }
        return Promise.resolve([]);
    }

    const {data: complementaryArticles} = await useAsyncData('complementary-articles', fetchArticles, {deep: true});

    if (complementaryArticles.value) {
        const mostRecentComplementaryArticles = complementaryArticles.value
            .map((query) => query?.results)
            .flat()
            .filter((article) => article !== undefined)
            .sort((a, b) => new Date(b?.data?.releaseDate).getTime() - new Date(a?.data?.releaseDate).getTime())
            .slice(0, 3 - articles.length) as ArticleDocument[];
        articles.push(...mostRecentComplementaryArticles);
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .articles-list .slice-content {
        text-align: left;
    }

    .articles-list {
        background-color: var(--joy-color-quaternary-10);
        position: relative;

        .articles {
            &::after {
                content: '';
                background-image: url('~/assets/svg/rainbow.svg');
                background-position: center;
                background-repeat: no-repeat;
                position: absolute;
                width: 100%;
                max-width: 100%;
                left: 50%;
                top: 40%;
                transform: translate(-50%, -50%);
                height: 310px;
                z-index: 0;
                display: none;

                @include mq.screen_md(min) {
                    display: block;
                }
            }

            .articles-grid {
                display: grid;
                row-gap: var(--joy-core-spacing-5);
                grid-template-columns: minmax(0, 1fr);
                z-index: 1;
                position: relative;

                @include mq.screen_sm(min) {
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: var(--joy-core-spacing-4) var(--joy-core-spacing-3);

                    & > :last-child {
                        grid-column: 1 / -1;
                        max-width: 50%;
                        margin: 0 auto;
                    }
                }

                @include mq.screen_lg(min) {
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: var(--joy-core-spacing-6) var(--joy-core-spacing-4);
                    margin-bottom: 70px;

                    & > :last-child {
                        grid-column: auto;
                        max-width: 100%;
                    }
                }
            }
        }

        .content {
            .title p {
                text-align: center;
                color: var(--joy-color-primary-50);
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-secondary-200);
                margin-bottom: var(--joy-core-spacing-5);
            }

            .headline {
                text-align: center;
                margin-bottom: var(--joy-core-spacing-11);
            }

            .button-row {
                display: flex;
                justify-content: center;
                margin-top: var(--joy-core-spacing-12);

                @include mq.screen_sm(min) {
                    margin-top: 80px;
                }
            }
        }
    }
</style>
